import { LOCALE_CURRENCY } from "@/lib/locale-prefix";

/** Homepage product preview hero videos — numbered 1–3 to match onboarding tabs (WEB-573). */
export type ProductPreviewVideo = {
  readonly playbackId: string;
  readonly assetId?: string;
};

/** Per-tab clip overrides (tab index → clip), used on both desktop and mobile. */
export type ProductPreviewVideoOverrides = Partial<
  Record<0 | 1 | 2, ProductPreviewVideo>
>;

type LocalizedTab3Videos = {
  readonly USD: ProductPreviewVideo;
  readonly EUR: ProductPreviewVideo;
  readonly GBP: ProductPreviewVideo;
};

/** Tab 3 (Get approved for financing) — USD default; EUR/GBP per LOCALE_CURRENCY (WEB-1095, WEB-1097). */
const PRODUCT_PREVIEW_TAB3_DESKTOP_VIDEOS: LocalizedTab3Videos = {
  USD: {
    playbackId: "ArJHUxhCkSU00BVLJV5UrDQTNhN100FzCQMbiJss6xd5Y",
    assetId: "d6lRZSuoupq8gLhasBUFdFqdSj6K02lLKGTmxIjMUueU",
  },
  EUR: {
    playbackId: "qNmdlQWMHZHyMQSJFakyDBMpSxUzUH26fYKHyZ3NY7c",
    assetId: "1JqurGjeP007rhPgsopfVPwIMTlgUFkJdWBFlCsKE3TE",
  },
  GBP: {
    playbackId: "OgTUkUtcedr0034AYPKMAOofdkqDGI7q18xVjT3v0002Ms",
    assetId: "LOK1t6LSQHKLt01X5T023i6pSsvtMM2GxO7yXWR8sUeww",
  },
};

const PRODUCT_PREVIEW_TAB3_MOBILE_VIDEOS: LocalizedTab3Videos = {
  USD: {
    playbackId: "88Ls6dGVdVg5s01OwzW01tN84M6WaVcjRDrzJ02UP402ZXE",
    assetId: "xWa114rBGMBdZpdm1AlSoIOfDRM301YzEUyKMb02yyLuo",
  },
  EUR: {
    playbackId: "7TqOPYUx3N5cjZ00cLhdByVWhNOsU02um00gs01tSgtnSTg",
    assetId: "OelaDLHjGrC018GmNXq1YJ57HPydCKPNbNnoin1gTBDw",
  },
  GBP: {
    playbackId: "YzMkYrQGWGqI02J2Z02aHs01tsYC1I93XWXnWT438jR274",
    assetId: "5z9mH9ReHYSZxGLdmZ91aqoKBUjQKyiP02hJhBMZZW9Y",
  },
};

function tab3VideoForLocale(
  locale: string,
  variants: LocalizedTab3Videos
): ProductPreviewVideo {
  const currency = LOCALE_CURRENCY[locale] ?? "USD";
  if (currency === "EUR" || currency === "GBP") {
    return variants[currency];
  }
  return variants.USD;
}

/** Desktop hero clips for tabs 1–3; tab 3 swaps by locale currency (WEB-1095). */
export function getProductPreviewDesktopVideos(
  locale: string,
  overrides?: ProductPreviewVideoOverrides
): readonly ProductPreviewVideo[] {
  const tab3 =
    overrides?.[2] ??
    tab3VideoForLocale(locale, PRODUCT_PREVIEW_TAB3_DESKTOP_VIDEOS);
  return [
    overrides?.[0] ?? PRODUCT_PREVIEW_DESKTOP_VIDEOS[0],
    overrides?.[1] ?? PRODUCT_PREVIEW_DESKTOP_VIDEOS[1],
    tab3,
  ];
}

/** Mobile hero clips for tabs 1–3; tab 3 swaps by locale currency (WEB-1095). */
export function getProductPreviewMobileVideos(
  locale: string,
  overrides?: ProductPreviewVideoOverrides
): readonly ProductPreviewVideo[] {
  const tab3 =
    overrides?.[2] ??
    tab3VideoForLocale(locale, PRODUCT_PREVIEW_TAB3_MOBILE_VIDEOS);
  return [
    overrides?.[0] ?? PRODUCT_PREVIEW_MOBILE_VIDEOS[0],
    overrides?.[1] ?? PRODUCT_PREVIEW_MOBILE_VIDEOS[1],
    tab3,
  ];
}

export const PRODUCT_PREVIEW_DESKTOP_VIDEOS = [
  {
    playbackId: "WcuPmSWRBeGB1R63rapAt42oUGh02KXOb7b6n00EFr47Y",
    assetId: "Hv5ZCxdWHhJNtPxrVrw5PBa22R5bgDv6R5fCYAKgwmo",
  },
  {
    playbackId: "402PRKf181YyYkuLDed1xgRCmsM2DkXAedXg2LGAu6Zw",
    assetId: "r7d4aGSkw8VZb01fBBJpOULL6T75TY8p8HNH2bCVGZjA",
  },
  PRODUCT_PREVIEW_TAB3_DESKTOP_VIDEOS.USD,
] as const satisfies readonly ProductPreviewVideo[];

/** Mobile-specific hero clips — tab order matches PRODUCT_PREVIEW_DESKTOP_VIDEOS (WEB-738). */
export const PRODUCT_PREVIEW_MOBILE_VIDEOS = [
  {
    playbackId: "N5r3IQVPOlDQhwInmg8JsIcKtUgK32008SbihKrACVTQ",
    assetId: "IFDDFlqToWqwltzDcyEKcMly1Nt02mip6Hio01BAwDzB4",
  },
  {
    playbackId: "51WD001AuZISSDTPbHombMjGFF7CTJMJdGMj019MGLqDU",
    assetId: "3AzGNovMhn1ML8gqk4XOcHgVHQJBf5dXbKtoudsc00Js",
  },
  PRODUCT_PREVIEW_TAB3_MOBILE_VIDEOS.USD,
] as const satisfies readonly ProductPreviewVideo[];

/**
 * sm-only crop for mobile-source tabs 1–3 — centers the form UI in the card,
 * crops top dead space, keeps more of the bottom visible (WEB-738).
 */
export const PRODUCT_PREVIEW_MOBILE_SM_TAB_CROPS = [
  { objectPosition: "50% 24%", scale: 1.02 }, // Tell us about your business
  { objectPosition: "50% 28%", scale: 1.02 }, // Securely connect your platforms
  { objectPosition: "50% 28%", scale: 1.02 }, // Get approved for financing
] as const;

/** @deprecated Use PRODUCT_PREVIEW_MOBILE_SM_TAB_CROPS */
export const PRODUCT_PREVIEW_MOBILE_SM_TAB_POSITIONS: readonly (
  | string
  | undefined
)[] = PRODUCT_PREVIEW_MOBILE_SM_TAB_CROPS.map((crop) => crop.objectPosition);

/** @deprecated Use PRODUCT_PREVIEW_DESKTOP_VIDEOS or PRODUCT_PREVIEW_MOBILE_VIDEOS. */
export const PRODUCT_PREVIEW_VIDEOS = PRODUCT_PREVIEW_DESKTOP_VIDEOS;

export function productPreviewThumbnailUrl(playbackId: string) {
  return `https://image.mux.com/${playbackId}/thumbnail.webp?fit_mode=smartcrop&time=0`;
}
